/**
 * User actions are debounced with a 100ms delay.
 *
 * @module
 * @author Jacob Viertel <jv@onscreen.net>
 * @since 3.86.0
 */

import {CLICK, SUBMIT, off, on} from '@acng/frontend-bounty/dom/event.js';
import {DiscardingQueue} from '@acng/frontend-bounty/timing/discarding-queue.js';
import {IS, guard} from '@acng/frontend-rubicon';

import {PREFIX_BUSY, PREFIX_IDLE, SwapClass} from './style.js';

const MODULE = 'layout/service/worker';
const VERBOSE = false;
DEBUG: if (VERBOSE) console.warn('Import verbose', MODULE);

const STYLE_IDLE_CLICK = `${PREFIX_IDLE}${CLICK}`;
const STYLE_BUSY_CLICK = `${PREFIX_BUSY}${CLICK}`;

const toggleClickStyle = SwapClass([STYLE_BUSY_CLICK], [STYLE_IDLE_CLICK], []);

/**
 * @template {Element} T
 * @param {T} element
 * @param {(element: T & HTMLElement, evt: MouseEvent) => unknown} action
 */
export const onClick = (element, action) => {
  DEBUG: if (VERBOSE || element.hasAttribute('debug')) {
    console.debug(MODULE, 'add click listener', element, {action});
  }
  ASSERT: guard(element, IS(HTMLElement));

  /**
   * @param {MouseEvent} evt
   */
  const listener = (evt) => {
    DEBUG: if (VERBOSE || element.hasAttribute('debug')) console.info(MODULE, 'click', element, {action});
    queue(async () => {
      try {
        toggleClickStyle(element, true);
        await action(element, evt);
      } finally {
        toggleClickStyle(element, false);
      }
    });
  };

  toggleClickStyle(element, false);
  on(element, CLICK, listener);

  return () => {
    DEBUG: if (VERBOSE || element.hasAttribute('debug')) {
      console.debug(MODULE, 'remove click listener', element, {action});
    }
    off(element, CLICK, listener);
  };
};

const STYLE_IDLE_SUBMIT = `${PREFIX_IDLE}${SUBMIT}`;
const STYLE_BUSY_SUBMIT = `${PREFIX_BUSY}${SUBMIT}`;

const toggleSubmitStyle = SwapClass([STYLE_BUSY_SUBMIT], [STYLE_IDLE_SUBMIT], []);

/**
 * @param {Node} form
 *
 * @param {(form: HTMLFormElement, data: Record<string, FormDataEntryValue>, evt: SubmitEvent) => unknown} action
 */
export const onSubmit = (form, action) => {
  ASSERT: guard(form, IS(HTMLFormElement));
  DEBUG: if (VERBOSE || form.hasAttribute('debug')) {
    console.debug(MODULE, 'add submit listener', form, {action});
  }

  /**
   * @param {SubmitEvent} evt
   */
  const listener = (evt) => {
    DEBUG: if (VERBOSE || form.hasAttribute('debug')) console.info(MODULE, 'submit', form, {action});
    evt.preventDefault();
    queue(async () => {
      try {
        toggleSubmitStyle(form, true);
        await action(form, Object.fromEntries(new FormData(form)), evt);
      } finally {
        toggleSubmitStyle(form, false);
      }
    });
  };

  toggleSubmitStyle(form, false);
  on(form, SUBMIT, listener);

  return () => {
    DEBUG: if (VERBOSE || form.hasAttribute('debug')) {
      console.debug(MODULE, 'remove submit listener', form, {action});
    }
    off(form, SUBMIT, listener);
  };
};

const queue = DiscardingQueue(100, true);

DEBUG: if (VERBOSE) {
  queue.enableDebug(MODULE);
}
